import { IDateFormatProps } from "@blueprintjs/datetime";
import * as moment from "moment";
import { IEmpresa } from "./model";
var numeral = require('numeral');

/**
 * Formata um número de CNPJ.
 *
 * Este método transforma uma sequência numérica de 14 dígitos em um formato
 * legível, aplicando os separadores padrões de CNPJ (XX.XXX.XXX/XXXX-XX).
 *
 * @param {string} cnpj - O CNPJ da empresa como uma string numérica (14 dígitos).
 * @returns O CNPJ formatado como uma string no padrão "XX.XXX.XXX/XXXX-XX".
 */
export function formatCnpj(cnpj?: string | null): string {
  if (!cnpj) return "N/A";
  return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
}

/**
 * Formata um número de CPF.
 *
 * Este método transforma uma sequência numérica de 11 dígitos em um formato
 * legível, aplicando os separadores padrões de CPF (XXX.XXX.XXX-XX).
 *
 * @param {string} cpf - O CPF da empresa como uma string numérica (11 dígitos).
 * @returns O CPF formatado como uma string no padrão "XXX.XXX.XXX-XX".
 */
export function formatCpf(cpf: string): string {
  if(!cpf) return cpf
  return cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

/**
 * Ordena um array de objetos em ordem crescente com base em um campo de texto.
 *
 * @param {Array} ary - O array de objetos a ser ordenado.
 * @param {string} fieldName - O campo dos objetos pelo qual será feita a ordenação.
 * @returns Um novo array com os objetos ordenados.
 */
export function sortBy(ary: any[], fieldName: string): any[] {
  return ary.slice().sort((a, b) => {
    return a[fieldName].localeCompare(b[fieldName]);
  });
}

/**
 * Ordena um array de objetos em ordem decrescente com base em um campo de texto.
 *
 * @param {Array} ary - O array de objetos a ser ordenado.
 * @param {string} fieldName - O campo dos objetos pelo qual será feita a ordenação.
 * @returns Um novo array com os objetos ordenados em ordem decrescente.
 */
export function sortByDesc(ary: any[], fieldName: string): any[] {
  return ary.slice().sort((a, b) => {
    return b[fieldName].localeCompare(a[fieldName]);
  });
}

/**
 * Ordena um array de objetos em ordem crescente com base em um campo numérico.
 *
 * @param {Array} ary - O array de objetos a ser ordenado.
 * @param {string} fieldName - O campo numérico dos objetos pelo qual será feita a ordenação.
 * @returns Um novo array com os objetos ordenados.
 */
export function sortByNum(ary: any[], fieldName: string): any[] {
  return ary.slice().sort((a, b) => {
    if (a[fieldName] > b[fieldName]) {
      return 1;
    }
    if (a[fieldName] < b[fieldName]) {
      return -1;
    }
    return 0;
  });
}

/**
 * Agrupa objetos de um array com base no valor de uma chave específica.
 *
 * @param {Array} xs - O array de objetos a ser agrupado.
 * @param {string} key - A chave cujo valor será usado para agrupar os objetos.
 * @returns Um objeto onde cada chave representa um grupo e contém os objetos agrupados.
 */
export function groupBy(xs: any[], key: string) {
  return xs.reduce(function (rv: any, x: any) {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
}

/**
 * Calcula a soma dos valores de uma chave específica em um array de objetos.
 *
 * @param {Array} xs - O array de objetos contendo os valores.
 * @param {string} key - A chave cujo valor será somado.
 * @returns A soma total dos valores encontrados na chave especificada.
 */
export function sum(xs: any[], key: string): number {
  return xs.reduce(function (rv: number, x: any) {
    return rv + (x[key] || 0);
  }, 0);
}

/**
 * Retorna um formato de data configurado para utilização com Moment.js.
 *
 * @param {string} format - O formato de data desejado.
 * @returns Um objeto de configuração contendo formatadores e um placeholder.
 */
export function getMomentFormatter(format: string): IDateFormatProps {
  return {
    formatDate: (date, locale) => moment(date).locale(locale).format(format),
    parseDate: (str, locale) => moment(str, format).locale(locale).toDate(),
    placeholder: format,
  };
}

/**
 * Pausa a execução de uma função por um período definido.
 *
 * @param {number} ms - O tempo de pausa em milissegundos.
 * @returns Uma Promise que será resolvida após o tempo especificado.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Copia uma string para a área de transferência.
 *
 * @param {string} str - A string que será copiada.
 */
export function copyToClipboard(str: string) {
  const el = document.createElement("textarea");
  el.value = str;
  el.setAttribute("readonly", "");
  el.style.position = "absolute";
  el.style.left = "-9999px";
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
}

/**
 * Cria uma função debounce que adia a execução de uma função.
 *
 * @param {Function} func - A função a ser executada com debounce.
 * @param {number} wait - O tempo de espera antes da execução (em milissegundos).
 * @param {boolean} immediate - Se true, executa a função imediatamente na primeira chamada.
 * @returns Uma função debounce.
 */
export function debounce(func: Function, wait: number, immediate?: boolean) {
  var timeout: any;

  return function () {
    var context = this, args = arguments;
    var later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    var callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

/**
 * Valida um número de CNPJ verificando sua estrutura e dígitos verificadores.
 *
 * @param {string} cnpj - O número do CNPJ a ser validado (apenas números).
 * @returns Um booleano indicando se o CNPJ é válido.
 */
export function validarCNPJ(cnpj: string): boolean {
  cnpj = cnpj.replace(/[^\d]+/g, "");

  if (cnpj == "") return false;

  if (cnpj.length != 14) {
    return false;
  }

  if (
    cnpj == "00000000000000" ||
    cnpj == "11111111111111" ||
    cnpj == "22222222222222" ||
    cnpj == "33333333333333" ||
    cnpj == "44444444444444" ||
    cnpj == "55555555555555" ||
    cnpj == "66666666666666" ||
    cnpj == "77777777777777" ||
    cnpj == "88888888888888" ||
    cnpj == "99999999999999"
  ) {
    return false;
  }

  let tamanho = cnpj.length - 2;
  let numeros = cnpj.substring(0, tamanho);
  let digitos = cnpj.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += Number(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) {
      pos = 9;
    }
  }
  let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  if (resultado != Number(digitos.charAt(0))) {
    return false;
  }

  tamanho = tamanho + 1;
  numeros = cnpj.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += Number(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) {
      pos = 9;
    }
  }
  resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  if (resultado != Number(digitos.charAt(1))) {
    return false;
  }

  return true;
}

/**
 * Adiciona ou remove itens de uma lista com base no estado de marcação do item.
 *
 * @param {Object} item - O objeto que será avaliado para adição ou remoção.
 * @param {Array} itens - A lista de itens na qual o item será adicionado ou removido.
 * @param {Array} valores - Uma lista de IDs para verificar duplicação antes da adição.
 */
export function percorrerValores(item: any, itens: any[], valores: any[]) {
  const index = itens.findIndex(i => i.id === item.id);

  if (item.checked === 1 && valores.indexOf(item.id) === -1) {
    if (index === -1) {
      itens.push({
        id: item.id,
        header: item.name,
        xml: item.xml,
        campo: item.campo,
        order: item.order
      });
    }
  } else if (item.checked === 0 && index !== -1) {
    itens.splice(index, 1);
  }
}

/**
 * Retorna o nome do dia da semana com base em um número representando o dia.
 *
 * @param {string} dia - O número do dia da semana (1 para segunda-feira, 7 para domingo).
 * @returns O nome do dia da semana correspondente.
 */
export function getDiaSemana(dia) {
  switch (dia) {
    case "7": return "Domingo"
    case "1": return "Segunda-feira"
    case "2": return "Terça-feira"
    case "3": return "Quarta-feira"
    case "4": return "Quinta-feira"
    case "5": return "Sexta-feira"
    case "6": return "Sábado"
    default: return ""
  }
}

/**
 * Formata um valor de armazenamento em MB, GB ou TB.
 *
 * @param {number} valor - O valor em megabytes.
 * @returns Uma string representando o valor formatado com a unidade apropriada.
 */
export function formatarArmazenamento(valor) {
  if (valor < 1024) {
    return valor + " MB";
  } else if (valor < 1024 * 1024) {
    return (valor / 1024).toFixed(2) + " GB";
  } else if (valor < 1024 * 1024 * 1024) {
    return (valor / (1024 * 1024)).toFixed(2) + " TB";
  } else {
    return "";
  }
}

/**
 * Verifica se um CNPJ corresponde à matriz de uma empresa.
 *
 * @param {string} cnpj - O número do CNPJ (14 dígitos).
 * @returns True se o CNPJ for de uma matriz; caso contrário, false.
 */
export function isMatriz(cnpj) {

  const branchNumber = cnpj.substring(8, 12);

  return branchNumber === '0001';
}

/**
 * Valida os filtros de um objeto, verificando se todos os valores são strings não vazias.
 *
 * @param {Object} filtros - Um objeto contendo os filtros a serem validados.
 * @returns True se todos os filtros forem válidos; caso contrário, false.
 */
export function validarFiltros(filtros) {
  for (let chave in filtros) {
    if (filtros.hasOwnProperty(chave)) {
      if (typeof filtros[chave] !== 'string' || filtros[chave].trim() !== '') {
        return false;
      }
    }
  }
  return true;
}

/**
 * Retorna a máscara de inscrição estadual de acordo com o estado.
 *
 * @param {string} estado - O código do estado (ex: "RS", "SP").
 * @returns A máscara correspondente ao estado ou uma string vazia se o estado for desconhecido.
 */
export function mascara_inscricao_estadual(estado) {

  switch (estado) {
    case "RS":
      return "999/9999999"
    case "SP":
      return "999.999.999.999"
    case "MG":
      return "999.999.999/9999"
    case "PA":
      return "99.999.999-9"
    default:
      return ""
  }

}

/**
 * Decodifica uma string codificada em UTF-8.
 *
 * @param {string} str - A string codificada.
 * @returns A string decodificada em UTF-8.
 */
export function decodeUtf8String(str) {
  const bytes = new Uint8Array(str.split('').map(char => char.charCodeAt(0)));

  const decodedString = new TextDecoder('utf-8').decode(bytes);

  return decodedString;
}

/**
 * Formata e organiza os dados de acordo com os meses faltantes e status.
 *
 * Agrupa os dados com base no status e identifica os meses faltantes para cada status.
 * Retorna os dados ordenados por mês e formatados com o nome do mês e status.
 *
 * @param {Array} dados - Array de objetos contendo dados a serem processados.
 * @returns Array de objetos com dados ordenados e formatados por mês e status.
 */
export function handleFormat(dados: any) {

  let mesesFaltantes = []

  const dadosAgrupados = groupBy(dados, "status")

  Object.keys(dadosAgrupados).map(value => {

    let meses = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

    let dados = dadosAgrupados[value]

    dados.forEach(x => {

      meses = meses.filter(element => element !== x["mes"]);

    })

    meses.forEach(x => {

      mesesFaltantes.push({ count: 0, mes: x, status: parseInt(value) })

    })

  })

  let sorted = sortByNum(([...mesesFaltantes, ...dados]), "mes").map((item) => ({
    ...item,
    mes: format_mes(item.mes),
    status: format_status(item.status),
  }));

  return sorted

}

/**
 * Formata os dados de julgamento, agrupando-os por status e retornando a quantidade e os CNPJs associados.
 *
 * @param {Array} dados - Array de objetos contendo dados de julgamento.
 * @returns Array de objetos com informações sobre o status de julgamento, contagem e CNPJs associados.
 */
export function handleFormatJulgamento(dados) {

  const status = ["1", "3", "2", "4"];

  const statusCounts = { "1": 0, "3": 0, "2": 0, "4": 0 };

  const statusCountsEmp = { "1": [], "3": [], "2": [], "4": [] };

  dados.forEach(values => {
    if (statusCounts.hasOwnProperty(values.status)) {
      statusCounts[values.status] = values.count;
      statusCountsEmp[values.status] = values.cnpjs;
    }
  });

  const itens = status.map(x => ({
    item: format_status_julgamento(x),
    count: statusCounts[x],
    cnpjs: statusCountsEmp[x],
    status: x
  }));

  return itens;
}

/**
 * Formata o status do processo com base em um código numérico.
 *
 * @param {number} status - O código numérico representando o status.
 * @returns O nome do status correspondente.
 */
export function format_status(status) {

  switch (status) {
    case 0: return "Novo"
    case 1: return "Processando"
    case 2: return "Concluído"
    case 3: return "Processando"
    case 4: return "Concluído com Erros"
    case 7: return "Enviado à Fila"
    default: return "Não Disponível"
  }
}

/**
 * Formata o número do mês em uma abreviação de 3 letras.
 *
 * @param {number} mes - O número do mês (1 a 12).
 * @returns A abreviação do mês correspondente.
 */
export function format_mes(mes) {

  switch (mes) {
    case 1: return "Jan"
    case 2: return "Fev"
    case 3: return "Mar"
    case 4: return "Abr"
    case 5: return "Mai"
    case 6: return "Jun"
    case 7: return "Jul"
    case 8: return "Ago"
    case 9: return "Set"
    case 10: return "Out"
    case 11: return "Nov"
    case 12: return "Dez"
  }
}

/**
 * Formata o status de julgamento com base em um código.
 *
 * @param {string} status - O código de status de julgamento ("1", "3", "2").
 * @returns O nome do status correspondente.
 */
export function format_status_julgamento(status) {

  switch (status) {
    case "1": return "Sem Pendências"
    case "3": return "Exigibilidade Suspensa"
    case "2": return "Pendências"
    default: return "Erro leitura do Arquivo"
  }
}

/**
 * Converte uma string contendo números e separadores (vírgula ou ponto) para um número de ponto flutuante.
 *
 * @param {string} value - A string contendo o valor a ser convertido.
 * @returns O valor numérico convertido.
 */
export function parseToNumber(value) {
  const regex = /[\d,.]+/g;

  const matchedValue = value.match(regex)[0];

  if (matchedValue.includes(',')) {

    if (matchedValue.includes('.')) {

      return parseFloat(matchedValue.replace(/,/g, '')); // Remove ',' e converte para número

    } else {

      return parseFloat(matchedValue.replace(/\./g, '').replace(',', '.')); // Remove '.' de milhares, substitui ',' por '.' e converte

    }

  } else {

    return parseFloat(matchedValue);

  }

}

export function filterByKeys(calcs, keys, conditions) {

  return calcs?.filter(calc => keys.every((key, index) => calc[key] === conditions[index]));

}

export function handleValidCalcs(resp_calcs: any, sub_tipo: string) {

  const calcs = resp_calcs

  const keys = ["simples_nacional", "simples_nacional_excesso", "regime_especial", "regime_e_sem_regime"];

  const conditionsMap = {
    "simples_nacional": [true, false, false, false],
    "simples_nacional_excesso": [true, true, false, false],
    "regime_especial": [false, false, true, false],
    "regime_e_sem_regime": [false, false, false, true],
  };

  const conditions = conditionsMap[sub_tipo];

  if (conditions) {

    return filterByKeys(calcs, keys, conditions);

  }

  if (sub_tipo === "outros") {

    return calcs?.filter(calc => {

      const trueCount = keys.filter(key => calc[key]).length;

      if (trueCount < 1 || trueCount > 2) {

        return true;

      }

      if (trueCount === 2) {

        if (calc.simples_nacional && calc.simples_nacional_excesso && !calc.regime_especial && !calc.regime_e_sem_regime) {

          return false;

        }

        return true;

      }

      return false;

    });

  }

  return [];

}

/**
 * Formata um mês numérico para o nome completo do mês.
 *
 * @param {string} mes - O mês no formato "01" a "12".
 * @returns O nome completo do mês correspondente.
 */
export function formaterMes(mes) {

  switch (mes) {
    case "01": return "Janeiro"
    case "02": return "Fevereiro"
    case "03": return "Março"
    case "04": return "Abril"
    case "05": return "Maio"
    case "06": return "Junho"
    case "07": return "Julho"
    case "08": return "Agosto"
    case "09": return "Setembro"
    case "10": return "Outubro"
    case "11": return "Novembro"
    case "12": return "Dezembro"
    default: return ""
  }
}

/**
 * Formata os dados de certidão com base no status e retorna as contagens e os CNPJs associados.
 *
 * @param {Array} dados - Array de objetos com informações de certidão.
 * @returns Array de objetos com status, contagem e CNPJs associados.
 */
export function handleFormatCertidao(dados) {

  const status = [0, 1, 2];

  const statusCounts = { 0: 0, 1: 0, 2: 0 };

  const statusCountsEmp = { 0: [], 1: [], 2: [] };

  dados.forEach(values => {
    if (statusCounts.hasOwnProperty(values.status)) {
      statusCounts[values.status] = values.count;
      statusCountsEmp[values.status] = values.cnpjs;
    }
  });

  const itens = status.map(x => ({
    item: format_status_certidao(x),
    count: statusCounts[x],
    cnpjs: statusCountsEmp[x],
    status: x
  }));

  return itens;
}

/**
 * Formata o status da certidão com base em um código numérico.
 *
 * @param {number} status - O código do status da certidão (0, 1, 2).
 * @returns O nome do status correspondente.
 */
export function format_status_certidao(status) {

  switch (status) {
    case 0: return "Certidão Negativa"
    case 1: return "Certidão Positiva"
    case 2: return "Certidão com Efeitos de Negativa"
    default: return "Erro leitura do Arquivo"
  }

}

/**
 * Valida se um endereço de email possui um formato válido.
 *
 * @param {string} email - O endereço de email a ser validado.
 * @returns True se o email for válido, caso contrário, false.
 */
export function isValidEmail(email) {

  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

  return emailPattern.test(email);

}

export function isValidPassword(password: string) {

  let length = /^.{6,}$/;
  let main = /[A-Z]/;
  let min = /[a-z]/;
  let number = /\d/;
  let caracter = /[!@#$%^&*()\-_+=\[{\]};:<>|./?]/;

  return (
    length.test(password) &&
    main.test(password) &&
    min.test(password) &&
    number.test(password) &&
    caracter.test(password)
  );

}

export function downloadBinaryFile(arrayBuffer: string, filename: string) {

  const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

}

export function format_status_nivy(status: string | number) {

  switch (status) {
    case null: return 0
    case "Novo": return 0
    case "Processando": return 1
    case "Pendente": return 1
    // case "Aguardando": return 1
    // case "Retentar": return 1
    case "Finalizado": return 2
    case "Completo": return 2
    case "Concluído": return 2
    case "Erro": return 3
    // case "Rejeitado": return 3
    // case "Parado": return 3
    case "Cancelado": return 8
    default: return status
  }

}

export function converterData(data: string): Date | null {

  if (!data) return null;

  return new Date(data);

}

export function get15thDayOfNextMonth(date: Date): Date {

  let year = date.getFullYear();

  let month = date.getMonth() + 1;

  if (month > 11) {
    month = 0;
    year++;
  }

  let fifteenthDay = new Date(year, month, 15);
  let dayOfWeek = fifteenthDay.getDay();

  if (dayOfWeek === 6) {
    fifteenthDay.setDate(14);
  }

  else if (dayOfWeek === 0) {
    fifteenthDay.setDate(16);
  }

  return fifteenthDay;
}

export function nameFile(empresa: IEmpresa, name: string) {

  return `${empresa.razao_social.replace(/\ /g, "_")}-${empresa.cnpj}-${name}.pdf`;

}

export function nameFileZip(empresa: IEmpresa, name: string) {

  return `${empresa.razao_social.replace(/\ /g, "_")}-${empresa.cnpj}-${name}.zip`;

}

export function format_tipo(tipo) {

  switch (tipo) {
    case "limite": return "Limite de Receita do Simples"
    case "sublimite": return "Sublimite de Receita"
    case "rpa": return "RPA"
    case "rba": return "RBA"
    case "rbt12": return "RBT12"
    case "iss": return "ISS"
    case "icms": return "ICMS"
    case "csll": return "CSLL"
    case "ipi": return "IPI"
    case "inss": return "INSS / CPP"
    case "irpj": return "IRPJ"
    case "cofins": return "COFINS"
    case "pis": return "PIS / Pasep"
    case "projecao": return "Projeção Linear da Receita"
  }

}

export function format_value_mva(value: string) {

  let dateObj = value

  if(!value) dateObj = "0,0"

  if (dateObj && dateObj.includes(",")) {

    dateObj = dateObj.replace(/\./g, "")

  } else if (dateObj && dateObj.includes(".")) {

    dateObj = dateObj.replace(/\./g, ",")

  }

  return dateObj

}

export function isExpired(dateString: string): boolean {

  const createdDate = new Date(dateString);
  const now = new Date();

  const diffInMs = now.getTime() - createdDate.getTime();
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  return diffInDays > 90;

};

export function format_imposto_peso(tipo) {

  switch (tipo) {
      case "iss": return "Peso do ISS"
    case "icms": return "Peso do ICMS"
    case "csll": return "Peso do CSLL"
    case "ipi": return "Peso do IPI"
    case "inss": return "Peso do INSS / CPP"
    case "irpj": return "Peso do IRPJ"
    case "cofins": return "Peso do COFINS"
    case "pis": return "Peso do PIS / Pasep"
  }

}

export const VARS = [
  {
    nome: "MVA da tabela",
    campo: "computed.mva",
    descricao:
      "Se tiver um produto vinculado ao item (NCM / CEST), o valor procurado da tabela MVA vinculado ao cálculo.",
  },
  {
    nome: "Alíquota ICMS da NFe se tiver, ou da tabela",
    campo: "computed.aliquota_icms_nota_ou_tabela",
    descricao:
      "Alíquota ICMS da NFe destacada na tag vICMS se tiver, senão, o valor da alíquota da tabela.",
  },
  {
    nome: "Base de cálculo do ICMS, ou valor do item",
    campo: "computed.base_icms_ou_valor_item",
    descricao:
      "Base do cálculo do ICMS destacada na NFe se tiver, senão, o valor do item.",
  },
  {
    nome: "Valor total da NFe",
    campo: "nota.valor",
    descricao: "Valor total da NFe.",
  },
  {
    nome: "Valor do frete da NFe",
    campo: "nota.valor_frete",
    descricao: "Valor do frete da NFe.",
  },
  {
    nome: "Valor do seguro da NFe",
    campo: "nota.valor_seguro",
    descricao: "Valor do seguro da NFe.",
  },
  {
    nome: "Valor das outras despesas da NFe",
    campo: "nota.valor_outro",
    descricao: "Valor das outras despesas da NFe.",
  },
  {
    nome: "Valor do ICMS do item da NFe",
    campo: "nota_item.valor_icms",
    descricao: "Valor do ICMS do item destacado na NFe se tiver, senão 0.",
  },
  {
    nome: "Valor do ICMS ST do item da NFe",
    campo: "nota_item.valor_icms_st",
    descricao: "Valor do ICMS ST do item destacado na NFe se tiver, senão 0.",
  },
  {
    nome: "Valor total do item da NFe",
    campo: "nota_item.valor",
    descricao: "Valor total do item da NFe.",
  },
  {
    nome: "Valor do IPI do item da NFe",
    campo: "nota_item.valor_ipi",
    descricao: "Valor do IPI do item da NFe.",
  },
  {
    nome: "Base de cálculo do ICMS do item da NFe",
    campo: "nota_item.bc_icms",
    descricao:
      "Base de cálculo do ICMS destacada no item da NFe se tiver, senão 0.",
  },
  {
    nome: "Base de cálculo do ICMS ST do item da NFe",
    campo: "nota_item.bc_icms_st",
    descricao: "Base de cálculo do ICMS ST do item da NFe se tiver, senão 0.",
  },
  {
    nome: "Alíquota do ICMS do estado de origem",
    campo: "nota.aliquota_icms_origem",
    descricao: "Alíquota do ICMS do estado de origem, procurada da tabela.",
  },
  {
    nome: "Alíquota do ICMS do estado de destino",
    campo: "nota.aliquota_icms_dest",
    descricao: "Alíquota do ICMS do estado de destino, procurada da tabela.",
  },
  {
    nome: "Alíquota do ICMS destacada na NFe",
    campo: "nota_item.aliquota_icms_nota",
    descricao: "Alíquota do ICMS destacada na NFe se tiver, senão 0.",
  },
  {
    nome: "Alíquota do produto ou tabela",
    campo: "computed.aliquota_interna_ou_tabela",
    descricao:
      "Alíquota do ICMS da tabela interna de produtos se tiver, senão a alíquota da tabela.",
  },
  {
    nome: "Valor desconto do produto",
    campo: "nota_item.vdesc",
    descricao: "Valor desconto do produto",
  },
  {
    nome: "Valor total do serviço(CTe)",
    campo: "cte.vtprest",
    descricao: "Valor total do serviço(CTe)",
  },
  {
    nome: "Valor base de cálculo(CTe)",
    campo: "cte.vbc",
    descricao: "Valor base de cálculo(Cte)",
  },
  {
    nome: "Alíquota ICMS CTe",
    campo: "cte.valiquota_icms",
    descricao: "Alíquota ICMS CTe",
  },
  {
    nome: "Valor do ICMS CTe",
    campo: "cte.vicms",
    descricao: "Valor do ICMS CTe",
  },
];

export function formatMillisToHMS(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => n.toString().padStart(2, '0');

  return `${pad(minutes)}min ${pad(seconds)}s`;
}

export function formatCpfOrCnpj(value) {

  console.log(value)

  if (!value) return "-"

  if (value.length == 11) {

    return formatCpf(value)

  }

  return formatCnpj(value)

}

export function gerarPeriodos([start, end]: [Date, Date]): string[] {

  const periodos: string[] = []

  const atual = new Date(start.getFullYear(), start.getMonth(), 1)
  const fim = new Date(end.getFullYear(), end.getMonth(), 1)

  while (atual <= fim) {
    const ano = atual.getFullYear()
    const mes = (atual.getMonth() + 1).toString().padStart(2, "0")

    periodos.push(`${ano}${mes}`)

    atual.setMonth(atual.getMonth() + 1)

  }

  return periodos

}

export const ITEM_LIST = [
  // "EMPRESAS",
  "PGDAS",
  "SPED",
  "FGTS DIGITAL",
  "IMPORTACAO",
  "XML INTEGRACAO",
  "MANIFESTACAO",
  "NOTAS",
  "SEFA",
  "PREFEITURA",
  "MEMORIA DE CALCULOS",
  "RESULTADOS DE CALCULOS",
  "LEGISLACAO",
  "CNDS",
  "RELATORIO GERAL",
  "SITFIS",
  "DEC SEFA",
  "CAIXA POSTAL",
  "CLASSIFICACAO",
  "CENTRAL DE ACOES",
  "GERENCIAMENTO DE TAREFAS",
  "LOGS",
  "PARCELAMENTOS",
  "DEI"
];

export function downloadFromBase64(base64, mime, filename) {

  const link = document.createElement("a");

  link.href = `data:application/${mime};base64,${base64}`;
  link.download = filename;
  link.click();

};

export const municipios_liberados = [
  "PARAUAPEBAS",
  "REDENCAO",
  "SANTANA DO ARAGUAIA",
  "XINGUARA"
]

export function formaterMesAbreviado(data) {

  const mes = data.split("-")[1]
  const ano = data.split("-")[0]

  switch (mes) {
    case "01": return `Jan/${ano}`
    case "02": return `Fev/${ano}`
    case "03": return `Mar/${ano}`
    case "04": return `Abr/${ano}`
    case "05": return `Mai/${ano}`
    case "06": return `Jun/${ano}`
    case "07": return `Jul/${ano}`
    case "08": return `Ago/${ano}`
    case "09": return `Set/${ano}`
    case "10": return `Out/${ano}`
    case "11": return `Nov/${ano}`
    case "12": return `Dez/${ano}`
    default: return data
  }
}

export function format_dia_semana(dia) {

  switch (dia) {
    case "1": return "toda Segunda-feira"
    case "2": return "toda Terça-feira"
    case "3": return "toda Quarta-feira"
    case "4": return "toda Quinta-feira"
    case "5": return "toda Sexta-feira"
    case "6": return "todo Sábado"
    case "7": return "todo Domingo"

    default: return "?"
  }

}

export const FAIXAS_BASE = [
  { faixa: 1, maximo: 100000, minimo: 0, valor: 19.9, armazenamento: 200 },
  { faixa: 2, maximo: 360000, minimo: 100000, valor: 29.9, armazenamento: 300 },
  { faixa: 3, maximo: 720000, minimo: 360000, valor: 49.9, armazenamento: 400 },
  { faixa: 4, maximo: 1800000, minimo: 720000, valor: 69.9, armazenamento: 500 },
  { faixa: 5, maximo: 3600000, minimo: 1800000, valor: 99.9, armazenamento: 800 },
  { faixa: 6, maximo: 4800000, minimo: 3600000, valor: 199.9, armazenamento: 1024 },
  { faixa: 7, maximo: 4980000, minimo: 4800000, valor: 229.9, armazenamento: 1024 },
  { faixa: 8, maximo: 5160000, minimo: 4980000, valor: 249.9, armazenamento: 1024 },
  { faixa: 9, maximo: 5520000, minimo: 5160000, valor: 269.9, armazenamento: 1024 },
  { faixa: 10, maximo: 6600000, minimo: 5520000, valor: 299.9, armazenamento: 1024 },
  { faixa: 11, maximo: 7800000, minimo: 6600000, valor: 399.9, armazenamento: 2048 },
  { faixa: 12, maximo: 9000000, minimo: 7800000, valor: 499.9, armazenamento: 2048 },
  { faixa: 13, maximo: 10000000, minimo: 9000000, valor: 699.9, armazenamento: 2048 },
];

export const LIMITE_MAXIMO = 10000000;